import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import Select from 'react-select'
import axios from 'axios'
import { fetchData } from '../../../utils/data.js'
import ClusterMap from '../../elem/map/ClusterMap'
import withConfig from '../../wrappers/withConfig'
import { format } from 'date-fns'
import PageNavigation from '../../../utils/PageNavigation'
import MapSearchResults from './MapSearchResults.js'
import urls from '../../../utils/constants/urls'
import { useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

const transformFromUpperLowerToNorthSouth = (selectedFeature) => {
    const { lowerLat, lowerLon, upperLat, upperLon } = selectedFeature
    return {
        north: upperLat,
        south: lowerLat,
        east: upperLon,
        west: lowerLon,
    }
}

const Search = ({ config, initialBoundingBox }) => {
    const apiURL = config.API_URL
    const cancelRef = useRef(null)

    const defaultCoordinates = useMemo(
        () => ({
            north: 49.382808,
            south: 24.521208,
            east: -66.945392,
            west: -124.736342,
        }),
        []
    )

    const selectedCoordinates = initialBoundingBox
        ? initialBoundingBox
        : defaultCoordinates

    // State management
    const [state, setState] = useState({
        query: '',
        mapResults: {},
        pageNo: 1,
        loading: false,
        message: '',
        selectedCoordinates: selectedCoordinates,
        defaultCoordinates: defaultCoordinates,
        mapCoordinates: defaultCoordinates,
        //state
        selectedState: null,
        stateNumber: '',
        stateNames: [],
        //county
        selectedCounty: null,
        countyNumber: '',
        countyNames: [],
        //city
        selectedCity: null,
        cityNames: [],
        cityName: '',
        //map
        stateLat: '',
        stateLon: '',
        //pagination
        totalResults: 0,
        totalPages: 0,
        currentPageNo: 0,
        resultsPerPage: 10,
        listResults: [],
    })

    // Utility functions
    const updateState = useCallback((updates) => {
        setState((prevState) => ({ ...prevState, ...updates }))
    }, [])

    const getPageCount = useCallback((total, denominator) => {
        const divisible = total % denominator === 0
        const valueToBeAdded = divisible ? 0 : 1
        return Math.floor(total / denominator) + valueToBeAdded
    }, [])

    // Map reset handler
    const onMapReset = useCallback(() => {
        updateState({
            selectedCoordinates: state.defaultCoordinates,
            mapCoordinates: state.defaultCoordinates,
        })
    }, [state.defaultCoordinates, updateState])

    // API functions
    const fetchListResults = useCallback(
        (north, south, east, west, pageNo) => {
            const listURL = `${apiURL}/fracfocus/wellsinbounds?north=${north}&south=${south}&east=${east}&west=${west}&page=${pageNo}&pagesize=${state.resultsPerPage}&orderby=WellName`

            axios
                .get(listURL)
                .then((res) => {
                    const total = res.data.Count
                    const resultNotFound = !res.data.Wells
                        ? 'There are no more search results.'
                        : ''

                    updateState({
                        listResults: res.data.Wells,
                        currentPageNo: pageNo,
                        message: resultNotFound,
                        loading: false,
                    })
                })
                .catch((error) => {
                    if (axios.isCancel(error) || error) {
                        updateState({
                            loading: false,
                            message:
                                'Failed to fetch list data. Please check network',
                        })
                        console.log(error)
                    }
                })
        },
        [apiURL, state.resultsPerPage, updateState]
    )

    // Pagination handler
    const handlePageClick = useCallback(
        (event, type) => {
            const updatedPageNo =
                type === 'prev'
                    ? state.currentPageNo - 1
                    : state.currentPageNo + 1

            if (!state.loading) {
                const { north, south, east, west } = state.mapCoordinates
                fetchListResults(north, south, east, west, updatedPageNo)
            }
        },
        [
            state.currentPageNo,
            state.loading,
            state.mapCoordinates,
            fetchListResults,
        ]
    )

    const fetchMapResults = useCallback(
        (north, south, east, west) => {
            const mapURL = `${apiURL}/fracfocus/wellsinbounds?north=${north}&south=${south}&east=${east}&west=${west}&orderby=WellName`

            if (cancelRef.current) {
                cancelRef.current.cancel()
            }
            cancelRef.current = axios.CancelToken.source()

            axios
                .get(mapURL, {
                    cancelToken: cancelRef.current.token,
                })
                .then((res) => {
                    const total = res.data.Count
                    const totalPagesCount = getPageCount(
                        total,
                        state.resultsPerPage
                    )
                    const resultNotFound = !res.data.Wells
                        ? 'There are no more search results.'
                        : ''

                    updateState({
                        mapResults: res.data.Wells,
                        message: resultNotFound,
                        loading: false,
                        totalResults: total,
                        currentPageNo: 1,
                        totalPages: totalPagesCount,
                    })
                })
                .catch((error) => {
                    if (axios.isCancel(error) || error) {
                        updateState({
                            loading: false,
                            message:
                                'Failed to fetch the data. Please check network',
                        })
                        console.log(error)
                    }
                })
        },
        [apiURL, getPageCount, state.resultsPerPage, updateState]
    )

    // Map change handler
    const handleMapChange = useCallback(
        (north, south, east, west) => {
            updateState({
                pageNo: 1,
                mapCoordinates: {
                    north: north,
                    south: south,
                    east: east,
                    west: west,
                },
            })

            fetchListResults(north, south, east, west, 1)
            fetchMapResults(north, south, east, west)
        },
        [fetchListResults, fetchMapResults, updateState]
    )

    // Data population functions
    const populateCounty = useCallback(
        (state_no) => {
            const countySearchURL = `${apiURL}/state/countiesbystateid?stateid=${state_no}`

            const then = (data) => {
                const resultNotFound = !data.length
                    ? 'There are no more search results.'
                    : ''
                updateState({
                    results: data,
                    message: resultNotFound,
                    loading: false,
                    countyNames: data.data,
                })
            }

            const onError = (error) => {
                updateState({
                    loading: false,
                    message: 'Failed to fetch the data. Please check network',
                })
                console.log(error)
            }

            fetchData(countySearchURL, then, onError)
        },
        [apiURL, updateState]
    )

    const populateCity = useCallback(
        (state_no, county_no) => {
            const citySearchURL = `${apiURL}/state/citiesbystateid?stateid=${state_no}`

            const then = (data) => {
                const resultNotFound = !data.length
                    ? 'There are no more search results.'
                    : ''
                updateState({
                    results: data,
                    message: resultNotFound,
                    loading: false,
                    cityNames: data.data,
                })
            }

            const onError = (error) => {
                updateState({
                    loading: false,
                    message: 'Failed to fetch the data. Please check network',
                })
                console.log(error)
            }

            fetchData(citySearchURL, then, onError)
        },
        [apiURL, updateState]
    )

    // Event handlers
    const handleOnInputChange = useCallback(
        (event) => {
            const query = event.target.value
            updateState({ query, loading: true, message: '' })
        },
        [updateState]
    )

    const handleStateChange = useCallback(
        (event) => {
            updateState({ selectedState: event })

            if (event != null) {
                updateState({
                    stateNumber: event.value,
                    selectedCity: '',
                    selectedCounty: '',
                    selectedCoordinates:
                        transformFromUpperLowerToNorthSouth(event),
                })
                populateCounty(event.value)
                populateCity(event.value)
            } else {
                updateState({
                    // api values
                    stateNumber: '',
                    countyNumber: '',
                    cityName: '',
                    // dropdown values
                    countyNames: [],
                    cityNames: [],
                    // form values
                    selectedState: '',
                    selectedCounty: '',
                    selectedCity: '',
                })
            }
        },
        [populateCounty, populateCity, updateState]
    )

    const handleCountyChange = useCallback(
        (event) => {
            updateState({ selectedCounty: event })

            if (event != null) {
                updateState({
                    selectedCity: '',
                    countyNumber: event.value,
                    selectedCoordinates:
                        transformFromUpperLowerToNorthSouth(event),
                })
            } else {
                updateState({
                    countyNumber: '',
                    selectedCity: '',
                })
            }
        },
        [updateState]
    )

    const handleCityChange = useCallback(
        (event) => {
            updateState({ selectedCity: event })

            if (event != null) {
                updateState({
                    selectedCounty: '',
                    countyNumber: '',
                    selectedCoordinates:
                        transformFromUpperLowerToNorthSouth(event),
                })
            } else {
                updateState({
                    cityName: '',
                    selectedCity: '',
                })
            }
        },
        [updateState]
    )

    // Fetch states function
    const fetchStates = useCallback(() => {
        const stateURL = `${apiURL}/state/states`

        const then = (data) => {
            updateState({ stateNames: data.data })
        }

        const onError = (error) => {
            console.log(error)
        }

        fetchData(stateURL, then, onError)
    }, [apiURL, updateState])

    // Effects
    useEffect(() => {
        fetchStates()
    }, [fetchStates])

    // Cleanup effect
    useEffect(() => {
        return () => {
            if (cancelRef.current) {
                cancelRef.current.cancel()
            }
        }
    }, [])

    // Render variables
    const {
        query,
        stateNames,
        selectedState,
        selectedCounty,
        countyNames,
        cityNames,
        selectedCity,
        loading,
        message,
        currentPageNo,
        totalPages,
        mapResults,
        listResults,
        totalResults,
    } = state

    const showPrevLink = 1 < currentPageNo
    const showNextLink = totalPages > currentPageNo
    const date = format(new Date(), 'MM-dd-yyyy')
    const showing_start =
        currentPageNo === 1 || currentPageNo === 0
            ? 1
            : state.resultsPerPage * (currentPageNo - 1) + 1
    const showing_end = state.resultsPerPage * currentPageNo

    return (
        <div className="search_panel">
            <Helmet>
                <title>Map Search</title>
                <link rel="icon" href="/favicon.ico" />
            </Helmet>
            <h2 className="findwells">Find a well in your area</h2>
            <form
                className="map_form"
                onSubmit={(event) =>
                    fetchMapResults(event, state.pageNo, state.query)
                }
            >
                <div className="map_search_panel">
                    <div className="map_advanced_input">
                        <label>State</label>
                        <Select
                            options={stateNames.map((x) => ({
                                label: x.StateName,
                                value: x.State_No,
                                lowerLat: x.LowerLat,
                                lowerLon: x.LowerLong,
                                upperLat: x.UpperLat,
                                upperLon: x.UpperLong,
                            }))}
                            value={selectedState}
                            onChange={handleStateChange}
                            isClearable={true}
                            placeholder="Select A State"
                             styles={{
                                placeholder: (base) => ({
                                ...base,
                                fontSize: '14px', 
                                }),
                            }}
                        />
                    </div>
                    <div className="map_advanced_input">
                        <label>County</label>
                        <Select
                            options={countyNames.map((x) => ({
                                label: x.CountyName,
                                value: x.Code,
                                lowerLat: x.LowerLat,
                                lowerLon: x.LowerLong,
                                upperLat: x.UpperLat,
                                upperLon: x.UpperLong,
                            }))}
                            value={selectedCounty}
                            onChange={handleCountyChange}
                            isClearable={true}
                            isDisabled={!selectedState}
                            placeholder="Select A County"
                            styles={{
                                placeholder: (base) => ({
                                ...base,
                                fontSize: '14px', 
                                }),
                            }}
                        />
                    </div>
                    <div className="map_advanced_input">
                        <label>City</label>
                        <Select
                            options={cityNames.map((x) => ({
                                label: x.CityName,
                                value: x.CityName,
                                lowerLat: x.LowerLat,
                                lowerLon: x.LowerLong,
                                upperLat: x.UpperLat,
                                upperLon: x.UpperLong,
                            }))}
                            value={selectedCity}
                            onChange={handleCityChange}
                            isClearable={true}
                            isDisabled={!selectedState}
                            placeholder="Select A City"
                             styles={{
                                placeholder: (base) => ({
                                ...base,
                                fontSize: '14px', 
                                }),
                            }}
                        />
                    </div>
                </div>
                <div className="return_adv">
                    <a
                        className="back_to_map"
                        href={`${urls.basename}/advanced`}
                    >
                        SWITCH TO ADVANCED SEARCH
                    </a>
                </div>
                <div className="cluster_map">
                    <ClusterMap
                        mapResults={mapResults}
                        handleMapChange={handleMapChange}
                        onReset={onMapReset}
                        defaultBoundingBox={state.defaultCoordinates}
                        initialBoundingBox={state.selectedCoordinates}
                        lat={state.stateLat ? state.stateLat : '39.8283'}
                        lon={state.stateLon ? state.stateLon : '-98.5795'}
                    />
                </div>
            </form>

            {/* Results Component  */}
           {totalResults && totalResults < 20000 ? (
                <MapSearchResults
                    mapResults={listResults}
                    totalhits={totalResults}
                />
            ) : (
                'Zoom in on map to see results.'
            )}

            {/* Pagination Component  */}
            {totalResults && totalResults < 20000 ? (
                <PageNavigation
                    totalPages={totalPages}
                    currentPage={currentPageNo}
                    loading={loading}
                    showPrevLink={showPrevLink}
                    showNextLink={showNextLink}
                    show_start={showing_start}
                    show_end={showing_end}
                    total={totalResults}
                    type="WELLS"
                    handlePrevClick={(event) => handlePageClick(event, 'prev')}
                    handleNextClick={(event) => handlePageClick(event, 'next')}
                />
            ) : (
                ''
            )}
        </div>
    )
}

const SearchWithDefaultBoundingBox = ({ config }) => {
    let [searchParams, setSearchParams] = useSearchParams()
    const boundingBoxParam = searchParams.get('bbox')
    const initialBoundingBox = useMemo(() => {
        const bbox = boundingBoxParam ? boundingBoxParam.split(',') : null
        if (bbox) {
            return {
                west: Number(bbox[0]),
                south: Number(bbox[1]),
                east: Number(bbox[2]),
                north: Number(bbox[3]),
            }
        }
        return null
    }, [boundingBoxParam])
    return <Search initialBoundingBox={initialBoundingBox} config={config} />
}

export default withConfig(SearchWithDefaultBoundingBox)
